import { useReducer, useState, useCallback, useEffect } from 'react';
import customerFormReducer from 'containers/pages/customers/reducers/customerFormReducer';
import { initCustomer, initCustomerValid, loadedCustomerValid } from 'containers/pages/customers/data';
import { handleCustomer, makeCustomerObj, convertCustomer } from 'containers/pages/customers/libs';
import { getCustomer } from 'api/customer';
import * as TY from 'containers/pages/customers/constants';
import { useNotificationProvider } from 'hooks';
import { CustomerFormData, CustomerValidation } from 'containers/pages/customers/types';

interface UseCustomerFormUnifiedProps {
  mode: 'add' | 'edit';
  customerId?: number | null;
  onSave?: (formData: Record<string, unknown>, isEdit: boolean) => Promise<void>;
  onClose?: () => void;
}

function useCustomerFormUnified({ mode, customerId, onSave, onClose }: UseCustomerFormUnifiedProps) {
  const { notifyError } = useNotificationProvider();
  const [formState, dispatch] = useReducer(customerFormReducer, { ...initCustomer });
  const [valid, setValid] = useState<CustomerValidation>({ ...initCustomerValid });
  const [isLoading, setIsLoading] = useState(false);

  // Load customer data when editing
  useEffect(() => {
    if (mode === 'edit' && customerId) {
      const loadCustomer = async () => {
        setIsLoading(true);
        try {
          const result = await getCustomer(customerId);
          // API may return { Data: customer } or customer directly
          const customerData = result?.Data || result;
          const converted = convertCustomer(customerData);
          dispatch({ type: TY.CUSTOMER_CONVERT, payload: converted });
          setValid({ ...loadedCustomerValid });
        } catch (error) {
          console.error('[useCustomerFormUnified] Failed to load customer:', error);
          notifyError('Failed to load customer data');
        } finally {
          setIsLoading(false);
        }
      };
      loadCustomer();
    }
  }, [mode, customerId, notifyError]);

  // Central field change handler
  const onForm = useCallback(
    (e: { target: { name: string; value: string } }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      handleCustomer(e, dispatch, setValid as any);
    },
    [],
  );

  // Handle CustomerType autocomplete change (special - not through handleCustomer)
  const onTypeChange = useCallback(
    (_event: unknown, newValue: { Id: number; Title: string } | null) => {
      if (newValue) {
        dispatch({ type: TY.TYPE_ID, payload: newValue.Id });
        dispatch({ type: TY.TYPE_TITLE, payload: newValue.Title });
        setValid((prev) => ({
          ...prev,
          TypeId: { isErr: false, errMsg: '', canNotSave: false },
        }));
      } else {
        dispatch({ type: TY.TYPE_ID, payload: null });
        dispatch({ type: TY.TYPE_TITLE, payload: '' });
        setValid((prev) => ({
          ...prev,
          TypeId: { isErr: true, errMsg: 'Customer type is required', canNotSave: true },
        }));
      }
    },
    [],
  );

  // Compute canNotSave
  const canNotSave = Object.values(valid).some((v) => v.canNotSave);

  // Save handler
  const onSaveForm = useCallback(async () => {
    // Final validation check before save
    const currentState = formState as CustomerFormData;
    let hasErrors = false;
    const newValid = { ...valid };

    if (!currentState.Name?.trim()) {
      newValid.Name = { isErr: true, errMsg: 'Customer name is required', canNotSave: true };
      hasErrors = true;
    }
    if (!currentState.TypeId) {
      newValid.TypeId = { isErr: true, errMsg: 'Customer type is required', canNotSave: true };
      hasErrors = true;
    }

    if (hasErrors) {
      setValid(newValid);
      return;
    }

    const payload = makeCustomerObj(currentState);
    if (onSave) {
      await onSave(payload, mode === 'edit');
    }
  }, [formState, valid, mode, onSave]);

  // Reset handler
  const onReset = useCallback(() => {
    if (mode === 'add') {
      dispatch({ type: TY.RESET, payload: initCustomer });
      setValid({ ...initCustomerValid });
    }
    // In edit mode, reload from API
    if (mode === 'edit' && customerId) {
      const reload = async () => {
        try {
          const result = await getCustomer(customerId);
          const customerData = result?.Data || result;
          const converted = convertCustomer(customerData);
          dispatch({ type: TY.CUSTOMER_CONVERT, payload: converted });
          setValid({ ...loadedCustomerValid });
        } catch (error) {
          console.error('[useCustomerFormUnified] Reset failed:', error);
        }
      };
      reload();
    }
  }, [mode, customerId]);

  const state = {
    customer: formState,
    valid,
    isLoading,
    canNotSave,
  };

  const handler = {
    onForm,
    onTypeChange,
    onSave: onSaveForm,
    onReset,
    dispatch,
  };

  return { state, handler };
}

export default useCustomerFormUnified;
